import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { entidadesApi, gruposApi, usuariosApi, referenciaApi, exportApi, perplexityApi, resolveAssetUrl } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { useSortable } from '../hooks/useSortable';
import { SortableHeader } from '../components/ui/SortableHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRow } from '../components/ui/SkeletonRow';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ImportadorCSV from '../components/ImportadorCSV';
import EmpresaAutocomplete from '../components/empresas/EmpresaAutocomplete';
import AnalisisInactividad from '../components/empresas/AnalisisInactividad';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Plus, Search, MapPin, Building2, Users as UsersIcon, ExternalLink, Briefcase, Upload, Download, Sparkles, X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const TIPO_LABEL = {
  distribuidor: 'Distribuidor',
  gestora: 'Gestora',
  administracion_publica: 'Admin. Pública',
  fabricante: 'Fabricante',
  otro: 'Otro',
};
const ESTADO_STYLE = {
  lead:      'bg-amber-50 border-amber-200 text-amber-700',
  prospecto: 'bg-blue-50 border-blue-200 text-blue-700',
  activo:    'bg-emerald-50 border-emerald-200 text-emerald-700',
  inactivo:  'bg-ink-100 border-ink-200 text-ink-500',
};
const ESTADO_LABEL = { lead: 'Lead', prospecto: 'Prospecto', activo: 'Activo', inactivo: 'Inactivo' };

const Clientes = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [entidades, setEntidades] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [comerciales, setComerciales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importDialog, setImportDialog] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [enrichDialog, setEnrichDialog] = useState(false);
  const [enrichCampos, setEnrichCampos] = useState([
    'web', 'telefono', 'email', 'direccion', 'cnae', 'sector', 'logo_url', 'empleados_aprox',
  ]);
  const [enrichModo, setEnrichModo] = useState('preguntar_conflictos');
  const [enrichLanzando, setEnrichLanzando] = useState(false);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      q: '', tipo: '', estado: '', provincia: '', grupo_id: '', comercial_asignado_id: '',
      zona_comercial: '', es_cliente: '', es_proveedor: '',
      pendiente_revision: params.get('pendiente_revision') === 'true' ? 'true' : '',
      es_lead: params.get('lead') === '1' ? 'true' : '',
    };
  });

  // Paginación servidor (Gap 2)
  const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const qDebounced = useDebounce(filters.q, 300);

  const cargar = async () => {
    setLoading(true);
    try {
      const payload = { ...filters };
      // Búsqueda 'q' → al backend (debounced)
      if (qDebounced) payload.q = qDebounced; else delete payload.q;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k];
        if (payload[k] === 'true') payload[k] = true;
        if (payload[k] === 'false') payload[k] = false;
      });
      payload.skip = page * pageSize;
      payload.limit = pageSize;
      const [resp, gs, provs] = await Promise.all([
        entidadesApi.paginated(payload),
        gruposApi.list().catch(() => []),
        referenciaApi.provincias().catch(() => []),
      ]);
      setEntidades(resp.items || []);
      setTotal(resp.total || 0);
      setGrupos(gs); setProvincias(provs);
      if (isAdmin) {
        try {
          const users = await usuariosApi.list({ rol: 'comercial', estado: 'activo' });
          setComerciales(users);
        } catch (_) { /* ignore */ }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    entidadesApi.zonasDisponibles().then(setZonas).catch(() => []);
  }, []);

  // Reset a página 0 cuando cambian filtros (que no sean la página)
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line
  }, [
    filters.tipo, filters.estado, filters.provincia, filters.grupo_id, filters.comercial_asignado_id,
    filters.zona_comercial, filters.pendiente_revision, filters.es_lead, qDebounced,
  ]);

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [
    filters.tipo, filters.estado, filters.provincia, filters.grupo_id, filters.comercial_asignado_id,
    filters.zona_comercial, filters.pendiente_revision, filters.es_lead, qDebounced, page, pageSize,
  ]);

  // Sin filtrado in-memory por 'q' — ahora va al servidor
  const filtered = entidades;

  // Ordenación cliente (M3)
  const { sorted, sortKey, sortDir, toggle } = useSortable(
    filtered,
    'razon_social',
    'asc',
    {
      grupo: (e) => e.grupo_nombre || '',
      tipo: (e) => TIPO_LABEL[e.tipo] || e.tipo || '',
      estado: (e) => ESTADO_LABEL[e.estado] || e.estado || '',
      contactos: (e) => e.num_contactos ?? 0,
    }
  );

  return (
    <div data-testid="clientes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Empresas</h1>
          <p className="text-ink-500 mt-1">
            Grupos, empresas (clientes y/o proveedores) y contactos del sector agua — con filtro por provincia.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(user?.rol === 'admin' || user?.rol === 'dir_comercial') && (
            <>
              <AnalisisInactividad />
              <Button
                variant="outline"
                onClick={async () => { try { await exportApi.descargar('empresas'); } catch (e) { alert(e.message); } }}
                data-testid="btn-export-empresas"
              >
                <Download className="w-4 h-4 mr-1.5" /> Exportar
              </Button>
              <Button
                variant="outline"
                onClick={() => setImportDialog(true)}
                data-testid="btn-importar-empresas"
                className="border-brand-200 text-brand-700 hover:bg-brand-50"
              >
                <Upload className="w-4 h-4 mr-1.5" /> Importar CSV
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  const soloSeleccionadas = selected.size > 0;
                  const msg = soloSeleccionadas
                    ? `Rellenar logos de las ${selected.size} empresas seleccionadas. Estrategia: Clearbit si hay web, avatar con iniciales si no. ¿Continuar?`
                    : 'Rellenar logos de TODAS las empresas (sin selección). Estrategia: Clearbit si hay web, avatar con iniciales si no. No gasta cuota Perplexity. ¿Continuar?';
                  if (!confirm(msg)) return;
                  try {
                    const ids = soloSeleccionadas ? Array.from(selected) : null;
                    const r = await perplexityApi.logosClearbitMasivo(false, 2000, true, ids);
                    const alcance = r.solo_seleccionadas ? `${selected.size} seleccionadas` : 'todas';
                    toast.success(`${r.actualizadas_total} logos rellenados en ${alcance} (${r.actualizadas_clearbit} Clearbit + ${r.actualizadas_avatar} avatar).`);
                    cargar();
                  } catch (e) { toast.error(e.message); }
                }}
                data-testid="btn-rellenar-logos"
                className="border-violet-200 text-violet-700 hover:bg-violet-50"
                title={selected.size > 0 ? `Aplicará sólo a las ${selected.size} empresas seleccionadas` : 'Aplicará a todas las empresas'}
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Rellenar logos{selected.size > 0 ? ` (${selected.size})` : ''}
              </Button>
            </>
          )}
          <Button
            className="bg-brand-500 hover:bg-brand-600 text-white shadow-sm"
            onClick={() => navigate('/clientes/nuevo')}
            data-testid="btn-nuevo-cliente"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nueva empresa
          </Button>
        </div>
      </div>

      <ImportadorCSV
        open={importDialog}
        onOpenChange={setImportDialog}
        tipo="empresas"
        titulo="Importar empresas desde CSV"
        onCompleted={cargar}
      />

      {/* Filtros */}
      <Card className="p-4 mb-6 border-ink-200">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <EmpresaAutocomplete
              value={filters.q}
              onChange={(v) => setFilters({ ...filters, q: v })}
              onPick={(it) => navigate(`/clientes/${it.id}`)}
              placeholder="Buscar por nombre, CIF, ERP, localidad…"
              testid="filter-q-autocomplete"
            />
          </div>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
            value={filters.es_cliente}
            onChange={(e) => setFilters({ ...filters, es_cliente: e.target.value })}
            data-testid="filter-rol">
            <option value="">Cliente/Proveedor: todos</option>
            <option value="true">Solo clientes</option>
            <option value="false">No clientes</option>
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
            value={filters.tipo}
            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
            data-testid="filter-tipo">
            <option value="">Todos los tipos</option>
            {Object.entries(TIPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
            value={filters.es_lead}
            onChange={(e) => setFilters({ ...filters, es_lead: e.target.value })}
            data-testid="filter-lead">
            <option value="">Todos</option>
            <option value="true">🎯 Solo LEADs</option>
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
            value={filters.estado}
            onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
            data-testid="filter-estado">
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
            value={filters.provincia}
            onChange={(e) => setFilters({ ...filters, provincia: e.target.value })}
            data-testid="filter-provincia">
            <option value="">Todas las provincias</option>
            {provincias.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {zonas.length > 0 && (
            <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
              value={filters.zona_comercial}
              onChange={(e) => setFilters({ ...filters, zona_comercial: e.target.value })}
              data-testid="filter-zona">
              <option value="">Todas las zonas</option>
              {zonas.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          )}
          <select
            className={`h-10 rounded-md border text-sm px-3 ${filters.grupo_id ? 'border-brand-400 bg-brand-50 text-brand-700 font-medium' : 'border-ink-200 bg-white text-ink-700'}`}
            value={filters.grupo_id}
            onChange={(e) => setFilters({ ...filters, grupo_id: e.target.value })}
            data-testid="filter-grupo"
          >
            <option value="">Todos los grupos</option>
            {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
          </select>
          {isAdmin && (
            <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
              value={filters.comercial_asignado_id}
              onChange={(e) => setFilters({ ...filters, comercial_asignado_id: e.target.value })}
              data-testid="filter-comercial">
              <option value="">Todos los comerciales</option>
              {comerciales.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre} {c.apellidos || ''}</option>
              ))}
            </select>
          )}
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
            value={filters.pendiente_revision}
            onChange={(e) => setFilters({ ...filters, pendiente_revision: e.target.value })}
            data-testid="filter-pendiente-revision">
            <option value="">Todas (revisión)</option>
            <option value="true">⚠ Pendientes de revisión</option>
            <option value="false">Sólo revisadas</option>
          </select>
        </div>
      </Card>

      {/* Tabla */}
      <Card className="border-ink-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200 text-ink-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-2 py-3 w-8">
                  <input
                    type="checkbox"
                    className="accent-violet-600"
                    checked={filtered.length > 0 && filtered.every((e) => selected.has(e.id))}
                    onChange={(ev) => {
                      if (ev.target.checked) setSelected(new Set(filtered.map((e) => e.id)));
                      else setSelected(new Set());
                    }}
                    data-testid="select-all-clientes"
                  />
                </th>
                <SortableHeader label="Empresa"   sortKey="razon_social" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Rol"       sortKey="es_cliente"   currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Tipo"      sortKey="tipo"         currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Provincia" sortKey="provincia"    currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Grupo"     sortKey="grupo"        currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Estado"    sortKey="estado"       currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Contactos" sortKey="contactos"    currentKey={sortKey} dir={sortDir} onToggle={toggle} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100" data-testid="clientes-tbody">
              {loading && <SkeletonRow cols={8} count={8} />}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-0 py-0">
                    <EmptyState
                      compact
                      icon={Building2}
                      title={entidades.length === 0 ? 'Aún no hay empresas' : 'Sin resultados con esos filtros'}
                      description={entidades.length === 0
                        ? 'Crea la primera con el botón de arriba o impórtalas desde CSV.'
                        : 'Prueba a quitar el filtro de provincia, grupo o estado.'}
                      testid="clientes-empty"
                    />
                  </td>
                </tr>
              )}
              {!loading && sorted.map((e) => (
                <tr
                  key={e.id}
                  className={`hover:bg-ink-50 ${selected.has(e.id) ? 'bg-violet-50/40' : ''}`}
                  data-testid={`row-cliente-${e.id}`}
                >
                  <td className="px-2 py-3" onClick={(ev) => ev.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="accent-violet-600"
                      checked={selected.has(e.id)}
                      onChange={(ev) => {
                        const next = new Set(selected);
                        if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                        setSelected(next);
                      }}
                      data-testid={`select-cliente-${e.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/clientes/${e.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center overflow-hidden">
                        {e.logo_url ? (
                          <img src={resolveAssetUrl(e.logo_url)} alt="" className="h-full w-full object-contain" onError={(ev) => { ev.target.style.display = 'none'; }} />
                        ) : (
                          <Building2 className="w-4 h-4" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-ink-900 leading-tight">
                          {e.nombre_comercial || e.razon_social}
                          {e.pendiente_revision && <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">⚠ pendiente</span>}
                          {e.es_lead && <span className="ml-2 text-[10px] bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded font-semibold" data-testid={`badge-lead-${e.id}`}>🎯 LEAD</span>}
                        </p>
                        <p className="text-xs text-ink-500 leading-tight">
                          {e.cif || '—'} {e.codigo_cliente_erp && <>· ERP {e.codigo_cliente_erp}</>}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {e.es_cliente && <Badge className="text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-700">Cliente</Badge>}
                      {e.es_proveedor && <Badge className="text-[10px] bg-violet-50 border border-violet-200 text-violet-700">Proveedor</Badge>}
                      {!e.es_cliente && !e.es_proveedor && <span className="text-ink-300 text-xs">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="border-ink-200 text-ink-700 bg-white">
                      <Briefcase className="w-3 h-3 mr-1" /> {TIPO_LABEL[e.tipo] || e.tipo}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-700">
                    <span className="inline-flex items-center gap-1 text-sm">
                      <MapPin className="w-3.5 h-3.5 text-ink-400" />
                      {e.provincia}{e.localidad && <span className="text-ink-500">· {e.localidad}</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-600 text-sm">
                    {e.grupo_nombre || <span className="text-ink-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`${ESTADO_STYLE[e.estado]} border`}>{ESTADO_LABEL[e.estado] || e.estado}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-sm text-ink-600">
                      <UsersIcon className="w-3.5 h-3.5" /> {e.num_contactos ?? 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Paginación servidor */}
        {!loading && total > 0 && (
          <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between flex-wrap gap-3 bg-ink-50/40">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-xs text-ink-600" data-testid="clientes-pagination-info">
                Mostrando <span className="font-semibold">{page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)}</span> de <span className="font-semibold">{total}</span> empresas
              </div>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                className="h-7 rounded border border-ink-200 bg-white text-xs px-2 text-ink-700"
                data-testid="clientes-page-size"
                aria-label="Empresas por página"
              >
                {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s} / pág.</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="clientes-prev-page"
              >
                ← Anterior
              </Button>
              <span className="text-xs text-ink-500">
                Página {page + 1} de {Math.max(1, Math.ceil(total / pageSize))}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={(page + 1) * pageSize >= total}
                onClick={() => setPage((p) => p + 1)}
                data-testid="clientes-next-page"
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Barra flotante de selección múltiple */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-ink-900 text-white shadow-xl rounded-full px-5 py-3 flex items-center gap-3 animate-fade-in" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selected.size} seleccionadas</span>
          <button
            onClick={() => setEnrichDialog(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5"
            data-testid="btn-enrich-bulk"
          >
            <Sparkles className="w-4 h-4" /> Enriquecer con Perplexity
          </button>
          <button onClick={() => setSelected(new Set())} className="text-ink-300 hover:text-white" title="Limpiar selección">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Dialog enriquecimiento masivo */}
      <Dialog open={enrichDialog} onOpenChange={setEnrichDialog}>
        <DialogContent className="max-w-xl" data-testid="dialog-enrich-bulk">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" /> Enriquecer {selected.size} empresas con Perplexity
            </DialogTitle>
            <DialogDescription>
              Perplexity buscará información pública verificada de cada empresa usando su CIF/razón social y rellenará los campos que marques.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-ink-700 mb-2 block">Campos a rellenar</label>
              <div className="grid grid-cols-2 gap-1">
                {['web', 'telefono', 'email', 'direccion', 'codigo_postal', 'localidad', 'provincia', 'pais', 'cnae', 'sector', 'empleados_aprox', 'facturacion', 'año_fundacion', 'logo_url', 'linkedin_url'].map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-ink-50 px-1 rounded">
                    <input
                      type="checkbox"
                      checked={enrichCampos.includes(c)}
                      onChange={(ev) => {
                        if (ev.target.checked) setEnrichCampos([...enrichCampos, c]);
                        else setEnrichCampos(enrichCampos.filter((x) => x !== c));
                      }}
                      className="accent-violet-600"
                      data-testid={`enrich-campo-${c}`}
                    />
                    <span className="text-ink-700">{c}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-700 mb-2 block">¿Qué hacemos si ya hay valor?</label>
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 cursor-pointer p-2 border border-ink-200 rounded hover:bg-ink-50">
                  <input type="radio" name="modo" value="solo_vacios" checked={enrichModo === 'solo_vacios'} onChange={(e) => setEnrichModo(e.target.value)} className="mt-0.5 accent-violet-600" />
                  <div>
                    <div className="text-sm font-medium">Solo rellenar campos vacíos</div>
                    <div className="text-xs text-ink-500">Si ya había un valor, no se toca. (Recomendado)</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 border border-ink-200 rounded hover:bg-ink-50">
                  <input type="radio" name="modo" value="preguntar_conflictos" checked={enrichModo === 'preguntar_conflictos'} onChange={(e) => setEnrichModo(e.target.value)} className="mt-0.5 accent-violet-600" />
                  <div>
                    <div className="text-sm font-medium">Preguntarme conflictos uno a uno</div>
                    <div className="text-xs text-ink-500">Aplica campos vacíos y acumula los conflictos en una lista para que los revises al terminar.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 border border-ink-200 rounded hover:bg-ink-50">
                  <input type="radio" name="modo" value="sobreescribir_todo" checked={enrichModo === 'sobreescribir_todo'} onChange={(e) => setEnrichModo(e.target.value)} className="mt-0.5 accent-violet-600" />
                  <div>
                    <div className="text-sm font-medium">Sobrescribir todo</div>
                    <div className="text-xs text-ink-500">Los valores de Perplexity reemplazan los actuales sin preguntar.</div>
                  </div>
                </label>
              </div>
            </div>
            <div className="p-3 bg-sky-50 border border-sky-200 rounded text-xs text-sky-900">
              💰 Coste estimado: ≈ <strong>{(selected.size * 0.01).toFixed(2)} €</strong> (1 consulta Perplexity por empresa).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrichDialog(false)}>Cancelar</Button>
            <Button
              disabled={enrichCampos.length === 0 || enrichLanzando}
              onClick={async () => {
                setEnrichLanzando(true);
                try {
                  const res = await perplexityApi.enriquecerMasivo({
                    entidad_ids: Array.from(selected),
                    campos: enrichCampos,
                    modo: enrichModo,
                  });
                  toast.success(`Job lanzado con ${res.total} empresas. Redirigiendo al progreso…`);
                  setEnrichDialog(false);
                  setSelected(new Set());
                  navigate(`/perplexity/jobs/${res.job_id}`);
                } catch (e) { toast.error(e.message); }
                finally { setEnrichLanzando(false); }
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="btn-lanzar-enrich"
            >
              {enrichLanzando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              Lanzar enriquecimiento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clientes;
